import { Injectable } from '@angular/core';

import {
  PracticeExam,
  PracticeExamDetails,
  ExamResult,
  ExamState,
  DrivingSkillCategory,
  DrivingSkill,
} from '@app/models/practiceExam';

type DrivingSkillRaw = {
  driving_skill: {
    data: {
      attributes: {
        humanized_category: string;
        name: string;
        max_grade: number;
      };
    };
  };
  grade: number | string;
};

type ExamData = {
  id: string;
  attributes: {
    started_at: string;
    comment?: string;
    teacher_name?: string;
    driving_skills?: DrivingSkillRaw[];
    result: {
      success: boolean;
      disqualifying_errors_number: number;
      total_score: number;
      max_score: number;
    };
  };
};

@Injectable({ providedIn: 'root' })
export class PracticeExamMapper {
  constructor() {}

  public mapExamDataToPracticeExamDetails(examData: ExamData): PracticeExamDetails {
    const baseExam = this.mapExamDataToPracticeExam(examData);
    const examResult = this.processResult(examData);
    const examState = this.processExamState(examResult);
    const teacherComments = examData?.attributes?.comment || null;
    const teacherName = examData?.attributes?.teacher_name || "L'enseignant";
    const drivingSkillsRaw = examData?.attributes?.driving_skills || [];
    const drivingSkillsSorted = this.processDrivingSkills(drivingSkillsRaw);

    return new PracticeExamDetails({
      ...baseExam,
      examResult,
      examState,
      teacherComments,
      teacherName,
      drivingSkillsSorted,
    });
  }

  public mapExamDataArrayToPracticeExamArray(examsData: ExamData[]): PracticeExam[] {
    if (!examsData?.length) {
      return [];
    }
    return examsData.map((exam) => this.mapExamDataToPracticeExam(exam));
  }

  private mapExamDataToPracticeExam(examData: ExamData): PracticeExam {
    return new PracticeExam({
      id: examData.id,
      date: new Date(examData.attributes.started_at),
      isSuccess: examData.attributes.result.success,
      disqualifyingFaults: examData.attributes.result.disqualifying_errors_number,
      totalScore: examData.attributes.result.total_score,
      maxScore: examData.attributes.result.max_score,
    });
  }

  private processResult(examData: ExamData): ExamResult {
    return {
      score: examData?.attributes?.result?.total_score || 0,
      max: examData?.attributes?.result?.max_score || 0,
      success: examData?.attributes?.result?.success || false,
      disqualifyingErrors: examData?.attributes?.result?.disqualifying_errors_number || 0,
    };
  }

  private processExamState(examResult: ExamResult): ExamState {
    return examResult.success
      ? { result: 'Réussi', icon: 'Check', color: 'accent-4' }
      : { result: 'Échoué', icon: 'Times', color: 'error-3' };
  }

  private processDrivingSkills(drivingSkillsRaw: DrivingSkillRaw[]): DrivingSkillCategory[] {
    const drivingSkills: DrivingSkillCategory[] = [];

    for (const skill of drivingSkillsRaw) {
      const existingCategoryIndex = drivingSkills.findIndex(
        (elt) => elt?.category === skill.driving_skill.data.attributes.humanized_category
      );

      if (existingCategoryIndex >= 0) {
        const newSubSkill = this.createNewSubSkill(skill);
        drivingSkills[existingCategoryIndex].subSkills.push(newSubSkill);
      } else {
        const newSkill = this.createNewCategory(skill);
        drivingSkills.push(newSkill);
      }
    }

    return drivingSkills;
  }

  private createNewCategory(skill: DrivingSkillRaw): DrivingSkillCategory {
    const subSkill = this.createNewSubSkill(skill);

    return {
      category: skill?.driving_skill?.data?.attributes?.humanized_category || 'Non classé',
      subSkills: [subSkill],
    };
  }

  private createNewSubSkill(skill: DrivingSkillRaw): DrivingSkill {
    const newSubSkill: DrivingSkill = {
      label: skill?.driving_skill?.data?.attributes?.name || 'Non défini',
      maxGrade: skill?.driving_skill?.data?.attributes?.max_grade || 0,
      grade: skill?.grade || 0,
      color: '',
      state: '',
    };

    newSubSkill.color =
      newSubSkill.grade === newSubSkill.maxGrade ? 'accent-4' : newSubSkill.grade === 'E' ? 'error-1' : 'primary-4';

    newSubSkill.state =
      newSubSkill.grade === newSubSkill.maxGrade
        ? 'Acquis'
        : newSubSkill.grade === 'E'
          ? 'Éliminé'
          : 'Partiellement acquis';

    return newSubSkill;
  }
}
